import { useLayoutEffect, useRef } from 'react';
import { DURATION, EASE, EXIT, arrive, enter, reducedMotion, stagger } from './motion';

/** More new items than this at once is a different list (a filter, a page), not arrivals. */
const ARRIVALS_MAX = 3;

interface Place {
  x: number;
  y: number;
  width: number;
  height: number;
  /** The element itself, for fading it out where it stood once React has removed it. */
  element: HTMLElement;
}

/**
 * An item that left: put back where it stood, out of the flow, and faded out,
 * while the items below slide up into its place (the FLIP above). A table row
 * on a desk cannot stand outside its table, so it simply goes; the rows still
 * close up smoothly. Request of 5 October 2026, section 6.
 */
function leave(container: HTMLElement, place: Place): void {
  const ghost = place.element;
  if (typeof ghost.animate !== 'function' || ghost.isConnected) return;
  if (getComputedStyle(container).display.startsWith('table')) return;
  ghost.setAttribute('aria-hidden', 'true');
  ghost.removeAttribute('data-motion-key');
  ghost.style.cssText += `;position:absolute;left:${String(place.x)}px;top:${String(place.y)}px;width:${String(place.width)}px;height:${String(place.height)}px;margin:0;pointer-events:none;`;
  if (getComputedStyle(container).position === 'static') container.style.position = 'relative';
  container.appendChild(ghost);
  const fade = ghost.animate(
    [
      { opacity: 1, transform: 'none' },
      { opacity: 0, transform: reducedMotion() ? 'none' : 'scale(0.97)' },
    ],
    { duration: EXIT.normal, easing: EASE.exit, fill: 'forwards' },
  );
  fade.onfinish = () => ghost.remove();
}

function childrenOf(container: HTMLElement): HTMLElement[] {
  return [...container.querySelectorAll<HTMLElement>('[data-motion-key]')].filter(
    (element) => element.parentElement === container,
  );
}

/**
 * Whether the list plays an item's arrival. An item marked
 * `data-motion-arrival="none"` plays its own: a message its writer just sent
 * rises from the composer with the conversation, and the highlight meant for
 * somebody else's news would only repeat it. It still slides when it moves and
 * still fades when it is removed. The request of 5 October 2026 (third),
 * section 4.
 */
function arrives(element: HTMLElement): boolean {
  return element.dataset.motionArrival !== 'none';
}

function onScreen(element: HTMLElement): boolean {
  const rect = element.getBoundingClientRect();
  return rect.bottom > 0 && rect.top < window.innerHeight;
}

/**
 * Motion for a list whose items carry `data-motion-key`. Request of 5 October
 * 2026, section 6:
 *
 * - on first appearance, the items on screen rise in with a small stagger, and
 *   only those: a long list does not animate what nobody can see;
 * - an item that arrives later (a new case, a note) rises in where it belongs
 *   with a short highlight that fades, so the eye finds it, unless it carries
 *   `data-motion-arrival="none"` and plays its own arrival;
 * - an item that moved (re-sorted, a status changed, one above it removed)
 *   slides to its new place instead of jumping (FLIP).
 *
 * A wholly different list, such as the next page or another filter, is treated
 * as a first appearance rather than as a crowd of arrivals.
 *
 * Positions are kept relative to the list, so scrolling between two renders is
 * not mistaken for movement. Nothing here delays the data: the list is drawn
 * in its final state first and the motion plays over it.
 */
export function useListMotion<T extends HTMLElement>(keys: readonly string[]) {
  const ref = useRef<T>(null);
  const known = useRef<Set<string> | null>(null);
  const places = useRef(new Map<string, Place>());
  const signature = keys.join('\u0000');

  useLayoutEffect(() => {
    const container = ref.current;
    if (container === null) return;
    const items = childrenOf(container);
    const origin = container.getBoundingClientRect();
    const now = new Map<string, Place>();
    for (const item of items) {
      const rect = item.getBoundingClientRect();
      now.set(item.dataset.motionKey ?? '', {
        x: rect.left - origin.left,
        y: rect.top - origin.top,
        width: rect.width,
        height: rect.height,
        element: item,
      });
    }

    const before = known.current;
    const kept =
      before === null ? 0 : items.filter((item) => before.has(item.dataset.motionKey ?? '')).length;
    const fresh = items.filter((item) => !(before?.has(item.dataset.motionKey ?? '') ?? false));

    if (before === null || (kept === 0 && items.length > 0)) {
      // A first appearance: the visible items, in sequence.
      stagger(items.filter(onScreen).filter(arrives));
    } else {
      const arrivals = fresh.length <= ARRIVALS_MAX && kept >= before.size / 2;
      // A few gone from a list still mostly there: they fade where they stood.
      const gone = [...places.current.entries()].filter(([key]) => !now.has(key));
      if (gone.length <= ARRIVALS_MAX && kept > 0) {
        for (const [, place] of gone) leave(container, place);
      }
      for (const item of fresh) {
        if (!arrives(item)) continue;
        if (arrivals) arrive(item);
        else enter(item, 'fade', { duration: DURATION.fast });
      }
      if (!reducedMotion()) {
        for (const item of items) {
          const key = item.dataset.motionKey ?? '';
          const was = places.current.get(key);
          const is = now.get(key);
          if (was === undefined || is === undefined || typeof item.animate !== 'function') continue;
          const dx = was.x - is.x;
          const dy = was.y - is.y;
          if (Math.abs(dx) < 1 && Math.abs(dy) < 1) continue;
          item.animate(
            [{ transform: `translate(${String(dx)}px, ${String(dy)}px)` }, { transform: 'none' }],
            { duration: DURATION.normal, easing: EASE.move },
          );
        }
      }
    }

    if (items.length > 0) known.current = new Set(now.keys());
    places.current = now;
  }, [signature]);

  return ref;
}
